/**
 * CIC camera image grid — EntityGrid-backed list of CIC (camera) captures for a site.
 *
 * Shows camera images from /api/odl-app/binary-files/ (file_type='cic-image').
 * Camera number and description are served by the binary-files descriptor; no
 * client-side camera join is needed. Supports thumbnail lightbox, delete action,
 * and navigation to device/user image views via a toolbar menu.
 */

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Button } from '@mui/material';
import { blue } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbox } from 'react-modal-image';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { fetchBinaryFiles } from '@sophocles/api/binaryFiles';
import { EntityGrid } from '@sophocles/data-grids';
import { deleteCameraImage } from '@sophocles/features/media/api';
import { normalizeBinaryFileRow } from '@sophocles/features/media/normalize';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

const CIC_COLUMN_VISIBILITY = { model: { actor_id: false, uploaded_by: false, comment: false } };

export default function CICImageTable() {
  const { cust_id, sitegroup_id, site_id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const openLightBox = useCallback(url => setLightboxUrl(url), []);

  const extraParams = useMemo(() => ({ file_type: 'cic-image', site_id }), [site_id]);

  const fetchFn = useCallback(async params => {
    const res = await fetchBinaryFiles(params);
    return { data: (res.data ?? []).map(normalizeBinaryFileRow), meta: res.meta };
  }, []);

  const fieldOverrides = useMemo(
    () => ({
      file_url: {
        renderCell: ({ row }) => (
          <img
            className="hover-div"
            src={row.file_url}
            width={104}
            height={104}
            style={{ cursor: 'pointer' }}
            onClick={() => openLightBox(row.file_url)}
          />
        ),
      },
      timestamp: {
        renderCell: ({ row }) => {
          if (!row.timestamp) return null;
          return <div style={{ overflow: 'wrap' }}>{format(new Date(row.timestamp), 'Pp')}</div>;
        },
      },
    }),
    [openLightBox],
  );

  const extraColumns = useMemo(
    () => [
      {
        field: 'delete_action',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            color="error"
            onClick={() =>
              confirmDelete(row, {
                entityLabel: t('general.image'),
                onConfirm: deletedRow => dispatch(deleteCameraImage(deletedRow.id)),
              })
            }
          >
            {t('actions.delete')}
          </Button>
        ),
      },
    ],
    [confirmDelete, dispatch, t],
  );

  const toolbarContent = (
    <>
      <IconButton style={{ height: '40px', padding: 0 }} onClick={e => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu open={menuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} onClick={() => setAnchorEl(null)}>
        <Link to={`/c/${cust_id}/plant/${sitegroup_id}/structure/${site_id}/deviceimages`}>
          <MenuItem style={{ color: blue[500] }}>{t('pages.deviceImages')}</MenuItem>
        </Link>
        <Link to={`/c/${cust_id}/plant/${sitegroup_id}/structure/${site_id}/images`}>
          <MenuItem>{t('media.userImages')}</MenuItem>
        </Link>
      </Menu>
    </>
  );

  const renderMobileCard = useCallback(
    row => (
      <div style={{ margin: '12px 8px', borderBottom: '1px solid grey', paddingBottom: 4 }}>
        <div>
          {t('media.timestampLabel')} {row.timestamp ? format(new Date(row.timestamp), 'P') : ''}
        </div>
        <div>
          <img
            className="image"
            src={row.file_url}
            width={104}
            height={104}
            style={{ cursor: 'pointer' }}
            onClick={() => openLightBox(row.file_url)}
          />
        </div>
        <div>
          {t('media.cameraLabel')} {row.camera_number}
        </div>
        <div>
          {t('media.cameraDescriptionLabel')} {row.camera_description}
        </div>
      </div>
    ),
    [openLightBox, t],
  );

  return (
    <>
      {lightboxUrl && <Lightbox medium={lightboxUrl} large={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
      {deleteConfirmDialog}
      <EntityGrid
        basePath="binary-files"
        fetchFn={fetchFn}
        extraParams={extraParams}
        fieldOverrides={fieldOverrides}
        options={{
          infiniteScroll: true,
          showSearch: true,
          persistKey: 'cicImagesGrid',
          columnVisibility: CIC_COLUMN_VISIBILITY,
          extraColumns,
          toolbarContent,
          mobile: { renderCard: renderMobileCard },
        }}
      />
    </>
  );
}
